import { Injectable, OnDestroy } from '@angular/core'
import { Facet, FacetCount } from 'smartsearch.js'
import { BehaviorSubject, combineLatest, Observable, Subscription } from 'rxjs'
import { ActivatedRoute } from '@angular/router'
import { CmsFacet, CmsFacetCount, ContentQuery } from '../../components/types'
import { MbConstants } from '../../../shared/mb-constants'

@Injectable({
  providedIn: 'root'
})
export class CmsFacetService implements OnDestroy {

  private readonly facetsOrder = ['Technology', 'Species', 'Cell type', 'Product type', 'Reagent type', 'Research area',
    'Starting material', 'Cell subtype',
    'Clinical application', 'Separation technology', 'Separation strategy', 'Primary antibody', 'Antigen', 'Clone', 'Isotype',
    'Type of antibody', 'Conjugate', 'Antibody application', 'MICS/IF fixation', 'Extended validation',
    'Flow cytometry application', 'Antibody clonality', 'Molecular class', 'Host', 'Tissue type', 'Laser',
    'Sample preparation application', 'Cytokine', 'Quality grade', 'Source', 'Application (media)', 'Stimulus', 'Peptide source',
    'ODN class', 'Target receptor', 'Signaling pathway', 'Molecular biology application', 'Methods in molecular biology',
    'Modalities', 'Structure', 'Job category', 'Level of experience', 'Country', 'Working time', 'Location', 'Company',
    'Division']
  private readonly hiddenFacets = ['Job_field', 'Category']

  private facets = new BehaviorSubject<Facet[]>([])
  private activeFacets = new BehaviorSubject<Facet[]>([])
  private readonlyFacets = new BehaviorSubject<CmsFacet[]>([])
  private areFacetsVisible = new BehaviorSubject<boolean>(false)
  private subscription = new Subscription()
  private query: string | undefined
  private queryContent: string | undefined
  private queryJob: string | undefined
  private search: string | undefined
  private searchFilter: string | undefined

  constructor (private route: ActivatedRoute) {
    this.init()
  }

  get facets$ (): Observable<Facet[]> {
    return this.facets.asObservable()
  }

  get activeFacets$ (): Observable<Facet[]> {
    return this.activeFacets.asObservable()
  }

  get readonlyFacets$ (): Observable<CmsFacet[]> {
    return this.readonlyFacets.asObservable()
  }

  get areFacetsVisible$ (): Observable<boolean> {
    return this.areFacetsVisible.asObservable()
  }

  init (): void {
    this.subscription.add(combineLatest([this.facets, this.route.queryParamMap])
      .subscribe(([facets, params]) => {
        const queryContent = (params.get(MbConstants.QUERY_JOB_PARAM) || params.get(MbConstants.QUERY_CONTENT_PARAM) || '')
          .split('&')
          .filter(facet => facet.length)
          .filter(facet => !facet?.startsWith(`facet.filter.${MbConstants.CATEGORY_FACET_CODE}=`))
        const activeFacets = facets?.map(facet => {
          const copy = JSON.parse(JSON.stringify(facet)) ?? {}
          copy.counts = (copy._counts ?? [])?.filter((count: any) => {
            if (queryContent.includes(decodeURI(count?.urlFilterQuery ?? ''))
              || queryContent.includes(unescape(count?.urlFilterQuery ?? ''))) {
              return count
            }
            return undefined
          }).filter((c: CmsFacet) => !!c)
          return copy.counts?.length ? copy : undefined
        }).filter(facet => !!facet)
        this.setActiveFacets(activeFacets)
      })
    )

    this.subscription.add(this.route.queryParamMap.subscribe(params => {
      this.query = params.get(MbConstants.QUERY_PARAM) || undefined
      this.queryContent = params.get(MbConstants.QUERY_CONTENT_PARAM) || undefined
      this.queryJob = params.get(MbConstants.QUERY_JOB_PARAM) || undefined
      this.search = params.get(MbConstants.SEARCH_PARAM) || undefined
      this.searchFilter = params.get(MbConstants.SEARCH_FILTER_PARAM) || undefined
    }))
  }

  getQueryParamWithFacetCount (facetCount: CmsFacetCount, jobs?: boolean): ContentQuery {
    if (!facetCount) {
      return {
        query: this.query, queryContent: this.queryContent, searchFilter: this.searchFilter, queryJob: this.queryJob, search: this.search
      }
    }
    const urlFilterQuery = this.getNormalizedUrlFilterQuery(facetCount)
    const queryContent = `${this.queryContent?.length ? this.queryContent + '&' : ''}${urlFilterQuery}`
    const queryJob = `${this.queryJob?.length ? this.queryJob + '&' : ''}${urlFilterQuery}`
    const query = {
      queryContent: jobs ? undefined : queryContent,
      queryJob: jobs ? queryJob : undefined
    } as ContentQuery
    if (this.query?.length) {
      query.query = this.query
    }
    if (this.search?.length) {
      query.search = this.search
    }
    if (this.searchFilter?.length) {
      query.searchFilter = this.searchFilter
    }
    return query
  }

  getQueryParamWithoutFacetCount (facetCount: CmsFacetCount, jobs?: boolean): ContentQuery {
    if (!facetCount) {
      return {
        query: this.query, queryContent: this.queryContent, searchFilter: this.searchFilter, queryJob: this.queryJob, search: this.search
      }
    }
    const urlFilterQuery = this.getNormalizedUrlFilterQuery(facetCount)
    const currentFilterWithoutFacetCount = (this.queryContent ?? '')
      .split('&')
      .filter(facet => !!facet)
      .filter(facet => facet !== urlFilterQuery)
      .join('&')
    const queryContent = currentFilterWithoutFacetCount?.trim()?.length ? currentFilterWithoutFacetCount : undefined
    const query = {
      queryContent: jobs ? undefined : queryContent,
      queryJob: jobs ? queryContent : undefined
    } as ContentQuery
    if (this.query?.length) {
      query.query = this.query
    }
    if (this.search?.length) {
      query.search = this.search
    }
    if (this.searchFilter?.length) {
      query.searchFilter = this.searchFilter
    }
    return query
  }

  isReadonlyFacet (facetCount: CmsFacetCount | FacetCount): boolean {
    return this.readonlyFacets.value.some(readonlyFacet => readonlyFacet.name === facetCount.value
      && (facetCount as CmsFacetCount).urlFilterQuery?.includes(`facet.filter.${readonlyFacet.category.replace(' ', '_')}`)
    )
  }

  setFacets (facets: Facet[] = []): void {
    const filteredFacets = facets.filter(facet => this.hiddenFacets.indexOf(facet.name) < 0)
    this.facets.next(this.sortAndFilterFacets(filteredFacets))
  }

  setReadonlyFacets (facets: CmsFacet[] = []): void {
    this.readonlyFacets.next(facets.filter(facet => facet.category !== MbConstants.CATEGORY_FACET_CODE))
  }

  setAreFacetsVisible (visible: boolean): void {
    this.areFacetsVisible.next(visible)
  }

  setActiveFacets (facets: Facet[] = []): void {
    this.activeFacets.next(facets)
  }

  ngOnDestroy (): void {
    this.subscription.unsubscribe()
  }

  getNormalizedUrlFilterQuery (facetCount: CmsFacetCount): string {
    return facetCount?.urlFilterQuery
      ? decodeURI(facetCount.urlFilterQuery.replaceAll('%2F', '/'))
      : ''
  }

  getFacetFromReadonlyFacet (readonlyFacet: CmsFacet): Facet {
    return {
      config: {
        baseName: readonlyFacet.category,
      },
      counts: [this.getFacetCountFromReadonlyFacet(readonlyFacet)],
    } as Facet
  }

  getFacetCountFromReadonlyFacet (readonlyFacet: CmsFacet): FacetCount {
    return {
      value: readonlyFacet.name,
      count: 0,
      filterQuery: `facet.filter.${readonlyFacet.category.replaceAll(' ', '_')}`,
      urlFilterQuery: `facet.filter.${readonlyFacet.category.replaceAll(' ', '_')}=${this.escapeFacetName(readonlyFacet.name)}`
    }
  }

  escapeFacetName (facetName: string): string {
    return facetName?.replaceAll(' ', '+') ?? ''
  }

  sortAndFilterFacets (facets: Facet[] = []): Facet[] {
    const additionalFacets = facets
      .filter(facet => this.facetsOrder.indexOf(this.getFacetName(facet)) < 0 && this.hiddenFacets.indexOf(facet.name) < 0)
    return this.facetsOrder.filter(orderItem => facets.findIndex(facet => this.getFacetName(facet) === orderItem) > -1)
      .map(orderItem => facets.find(facet => this.getFacetName(facet) === orderItem))
      .filter(facet => facet)
      .concat(additionalFacets) as Facet[]
  }

  private getFacetName (facet: Facet): string {
    return (facet?.config?.baseName ?? '').replaceAll('_', ' ')
  }
}
